"use client";

import * as React from "react";
import { Eye } from "lucide-react";

/**
 * Read-only chrome for the CRM agent "Dashboard Preview". Wraps the whole dashboard
 * when the session is a preview and makes it strictly VIEW-ONLY:
 *  - EVERY interaction (click / mousedown / keyboard-activate) is swallowed, EXCEPT
 *    clicks on real navigation links (<a href>), so the agent can still browse pages.
 *    We block by exclusion (not by tag list) because the dashboard's Edit/Add/Delete
 *    controls are often non-semantic (<div>/<span>/icon onClick) and a tag allow-list
 *    would let those through — which is exactly the leak this replaces.
 *  - a persistent ribbon marks it as a read-only agent view.
 * Data safety is ALSO enforced server-side: middleware refuses any non-GET request for
 * a preview session (all member mutations live under /api/member/*), so even a control
 * that somehow fires can never persist a change.
 */
export function PreviewInert({ memberName, children }: { memberName?: string; children: React.ReactNode }) {
  // A link counts as navigation ONLY if it stays inside the dashboard: a same-origin
  // http(s) URL whose path is under /my-account. This CONTAINS the preview — it blocks
  // escapes to the public site (logo -> "/", "Send a message" -> "/contact-us/",
  // "/sign-in/"), external links, and tel:/mailto:/sms: (which pop the OS dialer/mail app).
  // Everything the agent should reach (sidebar, breadcrumb, in-page anchors) lives under
  // /my-account, so nothing legitimate is lost.
  function isNavLink(target: EventTarget | null): boolean {
    const a = (target as HTMLElement | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a) return false;
    try {
      const u = new URL(a.getAttribute("href") || "", window.location.href);
      return (
        (u.protocol === "http:" || u.protocol === "https:") &&
        u.origin === window.location.origin &&
        // Exact "/my-account" or a real sub-path — the trailing-slash boundary stops a
        // future public route like "/my-account-signup" from counting as in-dashboard.
        (u.pathname === "/my-account" || u.pathname.startsWith("/my-account/"))
      );
    } catch {
      return false;
    }
  }

  // Block all pointer interaction except a PLAIN left-click that follows an in-dashboard
  // link. Middle-click, right-click, and modified (Ctrl/Cmd/Shift/Alt) clicks are always
  // blocked — they'd open the link in a NEW top-level tab, escaping the read-only iframe.
  function blockPointer(e: React.MouseEvent) {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (isNavLink(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
  }

  // auxclick fires for middle/right button; preventDefault here reliably suppresses the
  // middle-click "open in new tab" (which preventDefault-on-mousedown does NOT stop).
  function blockAux(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
  }

  // Block keyboard activation of controls (Enter / Space on a focused button/field),
  // but leave plain page keys (scrolling, tabbing) and in-dashboard links alone.
  function onKeyDownCapture(e: React.KeyboardEvent) {
    if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") return;
    if (isNavLink(e.target) && !(e.metaKey || e.ctrlKey || e.shiftKey)) return;
    const el = e.target as HTMLElement | null;
    if (el?.closest("a,button,input,select,textarea,[role='button'],[contenteditable='true']")) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  // Kill drag-and-drop file uploads (e.g. the Vault dropzone fires on `drop`, not click).
  function blockDrag(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
  }

  return (
    <div
      onClickCapture={blockPointer}
      onMouseDownCapture={blockPointer}
      onPointerDownCapture={blockPointer as unknown as React.PointerEventHandler}
      onAuxClickCapture={blockAux}
      onContextMenuCapture={blockAux}
      onKeyDownCapture={onKeyDownCapture}
      onDragOverCapture={blockDrag}
      onDropCapture={blockDrag}
      className="contents"
    >
      {children}
      <div className="fixed bottom-20 lg:bottom-4 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-2 rounded-full bg-navy-500 text-white shadow-lg px-4 py-2 text-[12.5px] font-semibold pointer-events-none">
        <Eye className="w-4 h-4" />
        Agent preview — read-only{memberName ? ` · ${memberName}` : ""}
      </div>
    </div>
  );
}
